/* ─────────────────────────────
   CANONICAL SHIFT LIFECYCLE
───────────────────────────── */

export const SHIFT_STATUS = Object.freeze({
  OPEN: "open",
  OFFERED: "offered",
  REQUESTED: "requested",
  TIME_REQUIRED: "time_required",

  ASSIGNED: "assigned",
  ACTIVATED: "activated",
  TRACKING: "tracking",
  ARRIVED: "arrived",

  COMPLETED: "completed",
  NO_SHOW: "no_show",
  CANCELLED: "cancelled",
  CANCELED: "canceled",
  CLOSED_UNFILLED: "closed_unfilled",
});

/*
 * A shift in one of these statuses is finished
 * operationally and must not return to an active
 * lifecycle without an explicit repair/migration.
 */
export const TERMINAL_SHIFT_STATUSES =
  new Set([
    SHIFT_STATUS.COMPLETED,
    SHIFT_STATUS.NO_SHOW,
    SHIFT_STATUS.CANCELLED,
    SHIFT_STATUS.CANCELED,
    SHIFT_STATUS.CLOSED_UNFILLED,
  ]);

/*
 * Demand that still requires a worker.
 */
export const OPEN_DEMAND_STATUSES =
  new Set([
    SHIFT_STATUS.OPEN,
    SHIFT_STATUS.OFFERED,
    SHIFT_STATUS.REQUESTED,
    SHIFT_STATUS.TIME_REQUIRED,
  ]);

/*
 * Worker-connected operational lifecycle.
 */
export const ACTIVE_SHIFT_STATUSES =
  new Set([
    SHIFT_STATUS.ASSIGNED,
    SHIFT_STATUS.ACTIVATED,
    SHIFT_STATUS.TRACKING,
    SHIFT_STATUS.ARRIVED,
  ]);

/*
 * Statuses from which a worker no-show may
 * potentially be valid.
 *
 * Additional checks such as assignment,
 * acceptance, start time, and arrival evidence
 * are still required.
 */
export const NO_SHOW_ELIGIBLE_STATUSES =
  new Set([
    SHIFT_STATUS.ASSIGNED,
    SHIFT_STATUS.ACTIVATED,
    SHIFT_STATUS.TRACKING,
  ]);

/* ─────────────────────────────
   NORMALIZATION
───────────────────────────── */

export function normalizeShiftStatus(
  value
) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export function normalizeShiftRole(
  value
) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

/* ─────────────────────────────
   TIME HELPERS
───────────────────────────── */

export function shiftValueToMillis(
  value
) {
  if (!value) {
    return NaN;
  }

  try {
    if (
      typeof value?.toDate ===
      "function"
    ) {
      return value
        .toDate()
        .getTime();
    }

    if (
      typeof value ===
      "number"
    ) {
      return value;
    }

    return new Date(
      value
    ).getTime();
  } catch {
    return NaN;
  }
}

export function getShiftStartMs(
  shift
) {
  return shiftValueToMillis(
    shift?.startTime ||
      shift?.start
  );
}

export function getShiftEndMs(
  shift
) {
  return shiftValueToMillis(
    shift?.endTime ||
      shift?.end
  );
}

export function getShiftHours(
  shift
) {
  const recordedHours = Number(
    shift?.hours ||
      shift?.hoursWorked ||
      shift?.payrollHours ||
      0
  );

  if (
    Number.isFinite(recordedHours) &&
    recordedHours > 0
  ) {
    return recordedHours;
  }

  const startMs =
    getShiftStartMs(shift);

  const endMs =
    getShiftEndMs(shift);

  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    startMs <= 0 ||
    endMs <= 0 ||
    endMs <= startMs
  ) {
    return 0;
  }

  return Math.round(
    ((endMs - startMs) /
      3600000) *
      100
  ) / 100;
}

export function shiftRangesOverlap(
  aStart,
  aEnd,
  bStart,
  bEnd
) {
  return (
    aStart < bEnd &&
    bStart < aEnd
  );
}

/* ─────────────────────────────
   ASSIGNMENT IDENTITY
───────────────────────────── */

/*
 * assignedWorkerUid is canonical.
 *
 * Remaining fields are legacy aliases that
 * remain readable during migration.
 */
export function getAssignedWorkerUid(
  shift
) {
  if (!shift) {
    return null;
  }

  const directUid =
    shift.assignedWorkerUid ||
    shift.assignedWorkerId ||
    shift.workerUid ||
    shift.workerId ||
    "";

  if (
    String(
      directUid || ""
    ).trim()
  ) {
    return String(
      directUid
    ).trim();
  }

  if (
    Array.isArray(
      shift.assignedWorkerUids
    ) &&
    shift.assignedWorkerUids
      .length > 0
  ) {
    const firstUid =
      String(
        shift
          .assignedWorkerUids[0] ||
          ""
      ).trim();

    return firstUid || null;
  }

  return null;
}

/* ─────────────────────────────
   ASSIGNMENT ACCEPTANCE
───────────────────────────── */

export function hasWorkerAcceptanceEvidence(
  shift,
  workerUid = null
) {
  if (!shift) {
    return false;
  }

  const assignedUid =
    workerUid ||
    getAssignedWorkerUid(
      shift
    );

  const acceptedWorkerUid =
    String(
      shift.acceptedWorkerUid ||
        ""
    ).trim();

  const workerResponseStatus =
    normalizeShiftStatus(
      shift.workerResponseStatus
    );

  const assignmentStatus =
    normalizeShiftStatus(
      shift.assignmentStatus
    );

  if (
    shift.assignmentAccepted ===
      true ||
    shift.acceptedByWorker ===
      true ||
    workerResponseStatus ===
      "accepted" ||
    assignmentStatus ===
      "accepted"
  ) {
    return true;
  }

  if (
    assignedUid &&
    acceptedWorkerUid &&
    acceptedWorkerUid ===
      assignedUid
  ) {
    return true;
  }

  return false;
}

/* ─────────────────────────────
   ARRIVAL EVIDENCE
───────────────────────────── */

export function hasShiftArrivalEvidence(
  shift
) {
  return Boolean(
    shift?.arrived === true ||
      shift?.arrivedAt ||
      shift?.arrivalConfirmedAt
  );
}

/* ─────────────────────────────
   STATUS CLASSIFICATION
───────────────────────────── */

export function isTerminalShiftStatus(
  value
) {
  return TERMINAL_SHIFT_STATUSES.has(
    normalizeShiftStatus(
      value
    )
  );
}

export function isOpenDemandShiftStatus(
  value
) {
  return OPEN_DEMAND_STATUSES.has(
    normalizeShiftStatus(
      value
    )
  );
}

export function isActiveShiftStatus(
  value
) {
  return ACTIVE_SHIFT_STATUSES.has(
    normalizeShiftStatus(
      value
    )
  );
}

export function isNoShowEligibleStatus(
  value
) {
  return NO_SHOW_ELIGIBLE_STATUSES.has(
    normalizeShiftStatus(
      value
    )
  );
}

/* ─────────────────────────────
   CANONICAL STATE SELECTOR
───────────────────────────── */

/*
 * Prefer lifecycleStatus only when present.
 * status remains canonical fallback while
 * legacy records are being migrated.
 */
export function getCanonicalShiftStatus(
  shift
) {
  if (!shift) {
    return "";
  }

  return normalizeShiftStatus(
    shift.lifecycleStatus ||
      shift.status
  );
}

/* ─────────────────────────────
   ACTIVATION WINDOW
───────────────────────────── */

export function getActivationWindowMinutes(
  shift,
  fallbackHours = 2
) {
  const hours =
    Number(
      shift?.activationWindowHours ??
        fallbackHours
    );

  const safeHours =
    Number.isFinite(hours)
      ? hours
      : fallbackHours;

  return Math.max(
    1,
    Math.round(
      safeHours * 60
    )
  );
}

/*
 * This is activation urgency, not the canonical
 * shift lifecycle status.
 *
 * Existing screens currently use labels such as
 * pending / reconfirm / warning / critical / late.
 */
export function computeShiftActivationUrgency(
  shift,
  nowMs = Date.now(),
  fallbackHours = 2
) {
  if (!shift) {
    return "pending";
  }

  const status =
    getCanonicalShiftStatus(
      shift
    );

  if (
    isTerminalShiftStatus(
      status
    )
  ) {
    return "pending";
  }

  if (
    hasShiftArrivalEvidence(
      shift
    )
  ) {
    return "pending";
  }

  const startMs =
    getShiftStartMs(
      shift
    );

  if (
    !Number.isFinite(
      startMs
    )
  ) {
    return "pending";
  }

  const diffMin =
    Math.round(
      (startMs - nowMs) /
        60000
    );

  const windowMin =
    getActivationWindowMinutes(
      shift,
      fallbackHours
    );

  if (
    diffMin > windowMin
  ) {
    return "pending";
  }

  if (diffMin > 60) {
    return "reconfirm";
  }

  if (diffMin > 30) {
    return "warning";
  }

  if (diffMin >= 0) {
    return "critical";
  }

  return "late";
}

export function computeShiftCountdown(
  shift,
  nowMs = Date.now(),
  fallbackHours = 2
) {
  const startMs =
    getShiftStartMs(
      shift
    );

  if (
    !Number.isFinite(
      startMs
    )
  ) {
    return {
      principal: "",
      secondary: "",
      withinWindow: false,
    };
  }

  const diffMin =
    Math.round(
      (startMs - nowMs) /
        60000
    );

  const windowMin =
    getActivationWindowMinutes(
      shift,
      fallbackHours
    );

  if (
    diffMin > windowMin
  ) {
    const hrs =
      Math.floor(
        diffMin / 60
      );

    const mins =
      diffMin % 60;

    return {
      principal:
        mins > 0
          ? `Starts in ${hrs}h ${mins}m`
          : `Starts in ${hrs}h`,

      secondary: "",

      withinWindow:
        false,
    };
  }

  if (diffMin >= 0) {
    let secondary = "";

    if (diffMin > 60) {
      secondary =
        "Within visibility window";
    } else if (
      diffMin > 30
    ) {
      secondary =
        "Less than 1 hour to shift start";
    } else {
      secondary =
        "Starts very soon";
    }

    return {
      principal:
        `T-${diffMin} min`,

      secondary,

      withinWindow:
        true,
    };
  }

  return {
    principal:
      `T+${Math.abs(
        diffMin
      )} min`,

    secondary:
      `Started ${Math.abs(
        diffMin
      )} min ago`,

    withinWindow:
      true,
  };
}